import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, ChevronDown, ChevronRight } from "lucide-react";
import { BACKEND_URL } from "../config";
import { useAuth } from "../modules/auth/authContext";

interface Props {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}

type MenuKey = "catalog" | "billing" | "employees" | "reports" | "contacts" | "patients" | "configuration";

const formatRoleLabel = (role?: string) => {
  if (!role) return "Viewer";
  return role
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const SidebarLayout: React.FC<Props> = ({ title, subtitle, children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, can, hasRole } = useAuth();
  const pathname = useLocation().pathname;
  const [expandedMenu, setExpandedMenu] = useState<MenuKey | null>(() => {
    if (pathname.startsWith("/patients")) return "patients";
    if (pathname.startsWith("/billing")) return "billing";
    if (pathname.startsWith("/employees")) return "employees";
    if (pathname.startsWith("/contacts")) return "contacts";
    if (pathname.startsWith("/reports")) return "reports";
    if (pathname.startsWith("/configuration")) return "configuration";
    if (pathname.startsWith("/medicines") || pathname.startsWith("/services") || pathname.startsWith("/labs")) return "catalog";
    return "catalog";
  });

  const navLinkClass = (path: string) => {
    const base = "block px-3 py-2 rounded-md text-sm font-medium";
    const active = "bg-primary text-white";
    const inactive = "text-subtext-light hover:bg-primary/10 hover:text-primary";
    return pathname === path ? `${base} ${active}` : `${base} ${inactive}`;
  };

  const catalogLinkClass = (path: string) => `${navLinkClass(path)} ml-5 py-1.5 text-xs`;

  const toggleMenu = (menu: MenuKey) => {
    setExpandedMenu((prev) => (prev === menu ? null : menu));
  };

  const canDashboard = can("dashboard.view");
  const canPatientIntake = can("patients.intake.view");
  const canPatientHistory = can("patients.history.view");
  const canContacts = can("contacts.view");
  const canEditContacts = can("contacts.edit");
  const canBilling = can("billing.view");
  const canEditBilling = can("billing.edit");
  const canInventory = can("catalog.view");
  const canEditCatalog = can("catalog.edit");
  const canEditBuyingPrice = can("medicine.buying_price.edit");
  const canEmployees = can("employees.view");
  const canEditEmployees = can("employees.edit");
  const canIncomeReport = can("reports.income.view");
  const canStockReport = can("reports.stock.view");
  const canReports = canIncomeReport || canStockReport;
  const canClinicalSuggestionsConfig = can("configuration.view");
  const canMinimumStockConfig = can("configuration.minimum_stock.view");
  const canConfiguration = canClinicalSuggestionsConfig || canMinimumStockConfig;
  const patientIntakeLabel = hasRole("doctor") ? "Consultation" : "Patient Intake";

  return (
    <div className="flex h-screen w-full max-w-full overflow-hidden print:block print:h-auto print:overflow-visible">
      {/* MOBILE BACKDROP */}
      <div
        className={`fixed inset-0 z-40 bg-black/40 lg:hidden transition-opacity ${
          sidebarOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* SIDEBAR */}
      <aside
        className={`fixed lg:static z-50 h-full w-64 bg-surface-light border-r border-border-light p-4 flex flex-col transform transition-transform duration-300 print:hidden ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        {/* LOGO + SIGN OUT */}
        <div className="mb-6 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white font-bold">
            PC
          </div>
          <div>
            <div className="font-bold text-lg">Clinic</div>
            <button
              onClick={async () => {
                try {
                  await fetch(`${BACKEND_URL}/auth/logout`, { method: "GET", credentials: "include" });
                  window.location.href = "/login";
                } catch (err) {
                  console.error("Logout failed:", err);
                }
              }}
              className="text-xs text-subtext-light hover:text-red-500 mt-1"
            >
              Sign Out
            </button>
          </div>
        </div>

        {/* MENU */}
        <nav className="flex-1 flex flex-col gap-2">
          {canDashboard && <Link to="/dashboard" className={navLinkClass("/dashboard")}>Dashboard</Link>}

          {/* Patients */}
          {(canPatientIntake || canPatientHistory) && (
            <>
              <button
                onClick={() => toggleMenu("patients")}
                className="flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md text-subtext-light hover:bg-primary/10 hover:text-primary"
              >
                <span>Patients</span>
                {expandedMenu === "patients" ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${expandedMenu === "patients" ? "max-h-40" : "max-h-0"}`}>
                <div className="ml-4 flex flex-col gap-1 mt-1">
                  {canPatientIntake && <Link to="/patients/intake" className={navLinkClass("/patients/intake")}>{patientIntakeLabel}</Link>}
                  {canPatientHistory && <Link to="/patients/history" className={navLinkClass("/patients/history")}>Patient History</Link>}
                </div>
              </div>
            </>
          )}

          {canInventory && (
            <>
              {/* Catalog */}
              <button
                onClick={() => toggleMenu("catalog")}
                className="flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md text-subtext-light hover:bg-primary/10 hover:text-primary"
              >
                <span>Catalog</span>
                {expandedMenu === "catalog" ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${expandedMenu === "catalog" ? "max-h-[520px]" : "max-h-0"}`}>
                <div className="ml-4 mt-1 flex flex-col gap-2 border-l border-border-light pl-3">
                  <div className="rounded bg-background-light px-2 py-1 text-xs font-bold uppercase tracking-wide text-subtle-light">Medicines</div>
                  <Link to="/medicines" className={catalogLinkClass("/medicines")}>View All</Link>
                  {canEditCatalog && <Link to="/medicines/new" className={catalogLinkClass("/medicines/new")}>Add New</Link>}
                  {canEditCatalog && <Link to="/medicines/bulk" className={catalogLinkClass("/medicines/bulk")}>Bulk Upload</Link>}
                  {canEditBuyingPrice && <Link to="/medicines/buying-price" className={catalogLinkClass("/medicines/buying-price")}>Buying Price</Link>}
                  <div className="rounded bg-background-light px-2 py-1 text-xs font-bold uppercase tracking-wide text-subtle-light">Services</div>
                  <Link to="/services" className={catalogLinkClass("/services")}>View All</Link>
                  {canEditCatalog && <Link to="/services/new" className={catalogLinkClass("/services/new")}>Add New</Link>}
                  <div className="rounded bg-background-light px-2 py-1 text-xs font-bold uppercase tracking-wide text-subtle-light">Labs</div>
                  <Link to="/labs" className={catalogLinkClass("/labs")}>View All</Link>
                  {canEditCatalog && <Link to="/labs/new" className={catalogLinkClass("/labs/new")}>Add New</Link>}
                </div>
              </div>
            </>
          )}

          {canBilling && (
            <>
              {/* Billing */}
              <button
                onClick={() => toggleMenu("billing")}
                className="flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md text-subtext-light hover:bg-primary/10 hover:text-primary"
              >
                <span>Billing</span>
                {expandedMenu === "billing" ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${expandedMenu === "billing" ? "max-h-32" : "max-h-0"}`}>
                <div className="ml-4 flex flex-col gap-1 mt-1">
                  {canEditBilling && <Link to="/billing/new" className={navLinkClass("/billing/new")}>New Bill</Link>}
                  <Link to="/billing" className={navLinkClass("/billing")}>View Existing Bills</Link>
                </div>
              </div>
            </>
          )}

          {canEmployees && (
            <>
              {/* Employee */}
              <button
                onClick={() => toggleMenu("employees")}
                className="flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md text-subtext-light hover:bg-primary/10 hover:text-primary"
              >
                <span>Employee</span>
                {expandedMenu === "employees" ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${expandedMenu === "employees" ? "max-h-56" : "max-h-0"}`}>
                <div className="ml-4 flex flex-col gap-1 mt-1">
                  <Link to="/employees" className={navLinkClass("/employees")}>View All</Link>
                  {canEditEmployees && <Link to="/employees/new" className={navLinkClass("/employees/new")}>Add New</Link>}
                  {canEditEmployees && <Link to="/employees/attendance" className={navLinkClass("/employees/attendance")}>Attendance</Link>}
                  {canEditEmployees && <Link to="/employees/shifts" className={navLinkClass("/employees/shifts")}>Shifts</Link>}
                  {canEditEmployees && <Link to="/employees/leaves" className={navLinkClass("/employees/leaves")}>Leaves</Link>}
                </div>
              </div>

            </>
          )}

          {canContacts && (
            <>
              {/* Contact */}
              <button
                onClick={() => toggleMenu("contacts")}
                className="flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md text-subtext-light hover:bg-primary/10 hover:text-primary"
              >
                <span>Contacts</span>
                {expandedMenu === "contacts" ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${expandedMenu === "contacts" ? "max-h-40" : "max-h-0"}`}>
                <div className="ml-4 flex flex-col gap-1 mt-1">
                  <Link to="/contacts" className={navLinkClass("/contacts")}>View All</Link>
                  {canEditContacts && <Link to="/contacts/new" className={navLinkClass("/contacts/new")}>Add New</Link>}
                </div>
              </div>
            </>
          )}

          {/* Reports */}
          {canReports && <button
            onClick={() => toggleMenu("reports")}
            className="flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md text-subtext-light hover:bg-primary/10 hover:text-primary"
          >
            <span>Reports</span>
            {expandedMenu === "reports" ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </button>}
          {canReports && <div className={`overflow-hidden transition-all duration-300 ${expandedMenu === "reports" ? "max-h-40" : "max-h-0"}`}>
            <div className="ml-4 flex flex-col gap-1 mt-1">
              {canIncomeReport && <Link to="/reports/income" className={navLinkClass("/reports/income")}>Income Report</Link>}
              {canStockReport && <Link to="/reports/stock" className={navLinkClass("/reports/stock")}>Stock Report</Link>}
            </div>
          </div>}

          {canConfiguration && (
            <>
              <button
                onClick={() => toggleMenu("configuration")}
                className="flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md text-subtext-light hover:bg-primary/10 hover:text-primary"
              >
                <span>Configuration</span>
                {expandedMenu === "configuration" ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${expandedMenu === "configuration" ? "max-h-32" : "max-h-0"}`}>
                <div className="ml-4 flex flex-col gap-1 mt-1">
                  {canClinicalSuggestionsConfig && <Link to="/configuration" className={navLinkClass("/configuration")}>Clinical Suggestions</Link>}
                  {canMinimumStockConfig && <Link to="/configuration/minimum-stock" className={navLinkClass("/configuration/minimum-stock")}>Minimum Stock</Link>}
                </div>
              </div>
            </>
          )}
        </nav>

        {/* USER SECTION */}
        <div className="mt-6 text-xs text-subtext-light">
          <div>Signed in as</div>
          <div className="font-medium">{user?.name || user?.email || "Guest"}</div>
          <div className="mt-1">
            Role: <span className="font-medium">{formatRoleLabel(user?.roleLabel || user?.role)}</span>
          </div>
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <main className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-8 print:block print:h-auto print:overflow-visible print:p-0">
        {/* MOBILE HEADER */}
        <div className="lg:hidden flex items-center justify-between mb-4 print:hidden">
          <button className="p-2 bg-primary text-white rounded" onClick={() => setSidebarOpen(true)}>
            <Menu size={22} />
          </button>
          <h1 className="text-xl font-bold">{title}</h1>
        </div>

        {/* DESKTOP HEADER */}
        <header className="hidden lg:flex justify-between items-center mb-6 print:hidden">
          <div>
            <h1 className="text-3xl font-black">{title}</h1>
            <p className="text-subtext-light">{subtitle || "Manage and track inventory & billing."}</p>
          </div>
        </header>

        {children}
      </main>
    </div>
  );
};

export default SidebarLayout;
